import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { SenderType } from "@/commons/models/Resident";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { AttachedFile } from "./OpinionMessages";

/**
 * ### 意見(待機者) API 操作クラス
 */
export default class WaiterOpinions {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET WaiterOpinions
   */
  public async gets(
    userStore: LoginUser,
    queryParams: {
      startIndex: number;
      endIndex: number;
      order: string;
    }
  ): Promise<Array<WaiterOpinionsGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterOpinions, formatParams);
    const response = await this.commonService.executeGet<Array<WaiterOpinionsGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      queryParams,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET WaiterOpinionsCount
   */
  public async getCount(userStore: LoginUser, queryParams?: { confirmed: boolean }): Promise<number> {
    // countの場合は末尾に「count」をつける
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterOpinions, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.IGNORE, 0);

    return response.data.count;
  }

  /**
   * ### GET OpinionMessages
   */
  public async getMessages(userStore: LoginUser, caseId: number): Promise<Array<WaiterOpinionMessagesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      caseId: caseId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterOpinionMessages, formatParams);
    const response = await this.commonService.executeGet<Array<WaiterOpinionMessagesGetModel>>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### POST OpinionMessages
   */
  public async postMessage(userStore: LoginUser, caseId: number, requestBody: WaiterOpinionMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      caseId: caseId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterOpinionMessages, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH WaiterOpinions
   */
  public async patchMessage(userStore: LoginUser, caseId: number, requestBody: WaiterOpinionMessagesPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      caseId: caseId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterOpinionMessages, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface WaiterOpinionsGetModel {
  /** ログの際に利用する (YYYYMMDDhhmmssSSS) */
  datetime: number | string;
  /** 施設を示すID */
  facilityId: number;
  /** メッセージの種別 */
  messageType: MessageType;
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: FunctionContents;
  /** LineのID */
  lineId: string;
  /** メールアドレス */
  mailaddress: string;
  /** 送信種別。LINE:0、MAIL:1 */
  senderType: SenderType;
  /** メッセージを受信した側のID */
  receiver: string;
  /** 利用者を示すID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 利用者の部屋名 */
  residentRoomName: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族名 */
  familyName: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** メッセージの送信時刻(YYYYMMDDhhmmssSSS) */
  sendTime: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 編集者 */
  writtenBy: string;
  /** 編集者のuserId */
  writerId: string;
  /** opinionのケースID */
  caseId: number;
  /** 利用者グループID */
  groupId: number;
}

/** メッセージ種別 */
export enum MessageType {
  TEXT = "text",
  IMAGE = "image",
  BUTTONS = "buttons",
  FLEX = "flex",
  FUNCTION = "function",
  MULTI = "multi"
}

/** 機能種別 */
export enum FunctionType {
  MESSAGE_LOG = 0,
  TODAY_STATUS = 1,
  SEND_PICTURE = 2,
  EVENT = 3,
  RESERVATION = 4,
  FACILITY = 5,
  OPINION = 6,
  SEND_MESSAGE = 7,
  SEND_MESSAGE_ADMIn = 8,
  DELETE_RESERVATION = 9,
  CONFIRM_OPINION = 10,
  EDIT_FAMILY_USER = 11,
  SEND_MESSAGE_OTHER_USER = 18,
  OTHER = 99
}

/** 機能種別毎の内容 */
export type FunctionContents = any;

/**
 * 意見メッセージ
 */
export interface WaiterOpinionMessagesGetModel {
  /** 施設ID */
  facilityId: number;
  /** ケースID */
  caseId: number;
  /** ソートキー{caseId}_{sendTime} */
  waiterOpinionMessagesSortKey?: string;
  /** 関係性 */
  familyRelation: string;
  /** 役割 */
  familyPosition: string;
  /** メッセージ内容 */
  content: string;
  /** 送信者 */
  sender: string;
  /** 送信種別() */
  senderType: SendType;
  /** 送信日時 */
  sendTime: number;
  /** 受信者 */
  receiver: string;
  /** 添付ファイル */
  attachedFiles: Array<AttachedFile> | null;
}

/** 意見の種類 */
export enum SendType {
  /** 家族 */
  FAMILY = 0,
  /** 施設 */
  FACILITY = 1
}

export interface WaiterOpinionMessagesPostModel {
  /** 利用者ID */
  residentId: number;
  /** 家族ID */
  familyId: number;
  /** 続柄 */
  familyRelation: string;
  /** 役割 */
  familyPosition: string | null;
  /** コンテンツ */
  content: string;
  /** 送信者ユーザーLINE ID */
  sender: string;
  /** 受信者ユーザーLINE ID */
  receiver: string;
  /** 送信種別 */
  senderType: SendType;
  /** 添付ファイルのS3URL */
  fileUrls: Array<string>;
  /** 添付ファイル名 */
  fileNames: Array<string>;
}

export interface WaiterOpinionMessagesPatchModel {
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: {
    familyRelation: string | null;
    familyPosition: string | null;
    content: string;
    finished: number | null;
  };
  /** LineのID */
  lineId: string;
  /** メッセージの種別 */
  messageType: MessageType;
  /** メッセージを受信した側のID */
  receiver: string;
  /** 利用者を示すID */
  residentId: number;
  /** 利用者名 */
  residentName: string;
  /** 利用者名（ふりがな） */
  residentKanaName: string;
  /** 利用者の部屋名 */
  residentRoomName: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族名 */
  familyName: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** メッセージの送信時刻(YYYYMMDDhhmmssSSS) */
  sendTime: number;
  /** 編集者 */
  writtenBy: string;
  /** 編集者のuserId */
  writerId: string;
  /** opinionのケースID */
  caseId: number;
}
